import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  fetchManualJson,
  fetchManualTranslationJson,
} from "../../apiServices/manualsApi";
import { useManualAuthoringStore } from "../../store/manualAuthoringStore";
import { useUserStore } from "../../store/userStore";
import BlockCanvasVendor from "./BlockCanvasVendor";
import ManualAuthoringHeaderReviewer from "./ManualAuthoringHeaderReviewer";
import ManualTOCReviewer from "./ManualTOCReviewer";
import "./ManualStructurePage.css";

export default function ManualStructurePageVendor() {
  const { manualId } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const user = useUserStore((s) => s.user);
  const loadManual = useManualAuthoringStore((s) => s.loadManual);
  const presentManual = useManualAuthoringStore((s) => s.presentManual);
  const selectedSectionId = useManualAuthoringStore((s) => s.selectedSectionId);
  const selectSection = useManualAuthoringStore((s) => s.selectSection);

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const parsedManualId = useMemo(() => {
    const id = Number(manualId);
    return Number.isNaN(id) ? 0 : id;
  }, [manualId]);

  const translationId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("translationId");
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [location.search]);

  useEffect(() => {
    selectSection(null);
  }, [selectSection]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!parsedManualId) {
        loadManual({ sections: [] });
        return;
      }

      setLoading(true);
      setError(null);
      try {
        if (translationId > 0) {
          const translatedManual = await fetchManualTranslationJson(
            oem,
            translationId
          );
          if (!mounted) return;
          loadManual(translatedManual ?? { sections: [] });
          return;
        }

        const result = await fetchManualJson(oem, parsedManualId);
        if (!mounted) return;
        loadManual(result.manual ?? { sections: [] });
      } catch {
        if (!mounted) return;
        loadManual({ sections: [] });
        setError(
          translationId > 0
            ? "Failed to load translated manual content."
            : "Failed to load manual content."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [loadManual, oem, parsedManualId, translationId]);

  const sections = presentManual?.sections ?? [];

  return (
    <div className="container-fluid manual-structure-page">
      <div className="manual-authoring-header__wrap">
        <ManualAuthoringHeaderReviewer />
      </div>
      <div className="manual-structure-page__body">
        <div className="row g-3 h-100">
          <div className="col-2 border-end p-2 manual-structure-page__toc-col">
            <div className="manual-structure-page__toc-scroll">
              <ManualTOCReviewer />
            </div>
          </div>

          <div className="col-10 p-3 manual-structure-page__main-col">
            <div className="manual-structure-page__main-scroll">
              {loading ? <div className="alert alert-info">Loading...</div> : null}
              {error ? <div className="alert alert-warning">{error}</div> : null}

              {!loading && !error ? (
                selectedSectionId ? (
                  <div className="d-flex justify-content-center">
                    <BlockCanvasVendor />
                  </div>
                ) : (
                  <div className="border rounded bg-white p-4">
                    {sections.length === 0 ? (
                      <div className="text-muted">
                        No sections available for this manual.
                      </div>
                    ) : (
                      <div className="text-muted">
                        Select a section from TOC to view content.
                      </div>
                    )}
                  </div>
                )
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
